import type { Chapter, Scenario, Scene } from '../../types';
import {
  buildChapterGroups,
  buildInlineStructure,
  createChapterStartScene,
  addInlineMission,
  addInlineChapter,
  collectChapterSceneIds,
  type InlineChapterGroup,
  type InlineMissionGroup,
} from '../../systems/inline-scene-model';

export {
  buildChapterGroups,
  buildInlineStructure,
  createChapterStartScene,
  addInlineMission,
  addInlineChapter,
  collectChapterSceneIds,
  type InlineChapterGroup,
  type InlineMissionGroup,
};

export function updateMissionTitle(scenario: Scenario, missionId: string, title: string): Scenario {
  return {
    ...scenario,
    metadata: {
      ...scenario.metadata,
      missions: (scenario.metadata.missions ?? []).map((m) =>
        m.id === missionId ? { ...m, title } : m
      ),
    },
  };
}

export function updateChapterTitle(scenario: Scenario, chapterId: string, title: string): Scenario {
  return {
    ...scenario,
    metadata: {
      ...scenario.metadata,
      chapters: (scenario.metadata.chapters ?? []).map((c) =>
        c.id === chapterId ? { ...c, title } : c
      ),
    },
  };
}

export function getSortedChapters(scenario: Scenario): Chapter[] {
  const chapters = scenario.metadata.chapters ?? [];
  const sceneIndexById = new Map(scenario.scenes.map((s, i) => [s.id, i]));
  return [...chapters].sort((a, b) => {
    const ia = sceneIndexById.get(a.startScene) ?? Number.MAX_SAFE_INTEGER;
    const ib = sceneIndexById.get(b.startScene) ?? Number.MAX_SAFE_INTEGER;
    return ia - ib;
  });
}

export function getNextChapterStartScene(scenario: Scenario, chapterId: string): string | undefined {
  const sorted = getSortedChapters(scenario);
  const idx = sorted.findIndex((c) => c.id === chapterId);
  if (idx < 0 || idx + 1 >= sorted.length) return undefined;
  return sorted[idx + 1]?.startScene;
}

export function removeInlineChapter(scenario: Scenario, chapterId: string): Scenario {
  const chapter = scenario.metadata.chapters?.find((c) => c.id === chapterId);
  if (!chapter) return scenario;

  const group = buildChapterGroups(scenario).find((g) => g.chapter?.id === chapterId);
  const removedSceneIds = group
    ? collectChapterSceneIds(scenario, group.scenes)
    : new Set(chapter.startScene ? [chapter.startScene] : []);

  const chapters = (scenario.metadata.chapters ?? []).filter((c) => c.id !== chapterId);
  const scenes = scenario.scenes.filter((s) => !removedSceneIds.has(s.id));

  const sceneIndexById = new Map(scenes.map((s, i) => [s.id, i]));
  const sortChapters = (list: Chapter[]) =>
    [...list].sort((a, b) => {
      const ia = sceneIndexById.get(a.startScene) ?? Number.MAX_SAFE_INTEGER;
      const ib = sceneIndexById.get(b.startScene) ?? Number.MAX_SAFE_INTEGER;
      return ia - ib;
    });

  let startScene = scenario.metadata.startScene;
  if (startScene && removedSceneIds.has(startScene)) {
    startScene = sortChapters(chapters)[0]?.startScene ?? scenes[0]?.id ?? '';
  }

  return {
    ...scenario,
    scenes,
    metadata: {
      ...scenario.metadata,
      chapters,
      startScene,
    },
  };
}

export type SceneKind =
  | 'inspection'
  | 'phone'
  | 'choice'
  | 'minigame'
  | 'chapterEnd'
  | 'dialogue'
  | 'mixed';

export function detectSceneKind(scene: Scene): SceneKind {
  if (scene.type === 'observation') return 'inspection';
  if (scene.type === 'clickableScreen') return 'inspection';
  if (scene.type === 'chapterEnd') return 'chapterEnd';
  if (['passwordGame', 'dragDropGame', 'situationSortGame'].includes(scene.type)) return 'minigame';
  if (['phoneCall', 'phoneChat', 'phoneMail', 'phoneMailInteract'].includes(scene.type)) return 'phone';
  if (scene.type === 'choice') return 'choice';
  if (scene.type === 'dialogue' || scene.type === 'command' || scene.type === 'timedOverlay') {
    return 'dialogue';
  }
  return 'mixed';
}

export const SCENE_KIND_LABELS: Record<SceneKind, string> = {
  inspection: 'Inspection',
  phone: 'Script interactif',
  choice: 'Dialogue à choix',
  minigame: 'Mini-jeu',
  chapterEnd: 'Fin de chapitre',
  dialogue: 'Dialogue autoguidé',
  mixed: 'Scène mixte',
};

export interface InlineSceneLocation {
  missionKey: string;
  chapterKey: string;
}

function containsInlineSceneId(scenes: Scene[], sceneId: string): boolean {
  for (const scene of scenes) {
    if (scene.id === sceneId) return true;
    if (scene.type === 'choice') {
      for (const option of scene.options ?? []) {
        if (option.scenes?.length && containsInlineSceneId(option.scenes, sceneId)) {
          return true;
        }
      }
    }
  }
  return false;
}

export function findInlineSceneLocation(
  scenario: Scenario,
  sceneId: string
): InlineSceneLocation | null {
  const structure = buildInlineStructure(scenario);

  for (let missionIndex = 0; missionIndex < structure.length; missionIndex++) {
    const missionGroup = structure[missionIndex]!;
    const missionKey = missionGroup.mission?.id ?? `ungrouped-${missionIndex}`;

    for (let chapterIndex = 0; chapterIndex < missionGroup.chapters.length; chapterIndex++) {
      const chapterGroup = missionGroup.chapters[chapterIndex]!;
      const chapterKey = chapterGroup.chapter?.id ?? `${missionKey}-chapter-${chapterIndex}`;
      if (containsInlineSceneId(chapterGroup.scenes, sceneId)) {
        return { missionKey, chapterKey };
      }
    }
  }

  return null;
}
